import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { UserResource } from '../../Data/UserResources/userResourceTypes';
import { fetchPendingOffers, reviewResourceOffer } from '../../api/userResources';
import { fetchAllSecurityUsers, type SecurityUserResponse } from '../../api/security';
import styles from './ContributionsPanel.module.css';

interface ContributionsPanelProps {
  projectGuid: string;
  projectName: string;
  canEdit?: boolean;
  debugMode?: boolean;
}

// Category → emoji map. Aligned with the unified 12-value taxonomy from
// migration 029 (user_resource_registry.category CHECK constraint). Keys
// must match the server's category enum or labels won't render.
// Audit D2 (Resources vocabulary, 2026-05-22): replaced legacy names
// (funding, land_space, food_water, certifications) with unified ones
// (financial, facilities, in_kind, knowledge, admin_legal). Kept in sync
// with ResourcesPanel.tsx — if you change one, change both.
const CATEGORY_EMOJI: Record<string, string> = {
  materials: '🧱',
  equipment: '🔧',
  services: '🤝',
  labor: '💪',
  transportation: '🚛',
  facilities: '🏞️',
  admin_legal: '📋',
  knowledge: '📜',
  financial: '💰',
  in_kind: '🎁',
  software_tech: '💻',
  other: '📦',
};

interface OfferWithStatus extends UserResource {
  isProcessing?: boolean;
}

interface PhotoLightboxState {
  isOpen: boolean;
  photos: Array<{ url: string; sort_order: number }>;
  currentIndex: number;
  resourceLabel: string;
}

export const ContributionsPanel: React.FC<ContributionsPanelProps> = ({
  projectGuid,
  projectName,
  canEdit = false,
  debugMode = false,
}) => {
  const { t } = useAppTranslation('pages');
  const [offers, setOffers] = useState<OfferWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    resourceOffers: true,
    roleApplications: false,
    contentSubmissions: false,
  });
  const [lightbox, setLightbox] = useState<PhotoLightboxState>({
    isOpen: false,
    photos: [],
    currentIndex: 0,
    resourceLabel: '',
  });
  const [replyNotes, setReplyNotes] = useState<Record<string, string>>({});
  const [userMap, setUserMap] = useState<Map<string, SecurityUserResponse>>(new Map());
  const lightboxRef = useRef<HTMLDivElement>(null);

  // Fetch pending offers and user directory on mount
  useEffect(() => {
    const loadOffers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [data, users] = await Promise.all([
          fetchPendingOffers(projectGuid),
          fetchAllSecurityUsers().catch(() => [] as SecurityUserResponse[]),
        ]);
        setOffers(data || []);
        const map = new Map<string, SecurityUserResponse>();
        for (const u of users) {
          const key = (u.userGuid || u.id || '').toUpperCase();
          if (key) map.set(key, u);
        }
        setUserMap(map);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load offers');
        setOffers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadOffers();
  }, [projectGuid]);

  /** Resolve a user GUID to a display name. */
  const resolveUserName = useCallback((guid: string | undefined): string => {
    if (!guid) return '—';
    const user = userMap.get(guid.toUpperCase());
    return user?.displayName || user?.name || guid.substring(0, 8);
  }, [userMap]);

  // Handle lightbox keyboard and click-outside
  useEffect(() => {
    if (!lightbox.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        navigatePhoto(-1);
      } else if (e.key === 'ArrowRight') {
        navigatePhoto(1);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (lightboxRef.current && e.target === lightboxRef.current) {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    lightboxRef.current?.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      lightboxRef.current?.removeEventListener('click', handleClickOutside);
    };
  }, [lightbox.isOpen, lightbox.currentIndex]);

  const openLightbox = (photos: Array<{ url: string; sort_order: number }>, resourceLabel: string) => {
    const sortedPhotos = [...photos].sort((a, b) => a.sort_order - b.sort_order);
    setLightbox({
      isOpen: true,
      photos: sortedPhotos,
      currentIndex: 0,
      resourceLabel,
    });
  };

  const closeLightbox = () => {
    setLightbox({
      isOpen: false,
      photos: [],
      currentIndex: 0,
      resourceLabel: '',
    });
  };

  const navigatePhoto = (direction: number) => {
    const newIndex = lightbox.currentIndex + direction;
    if (newIndex >= 0 && newIndex < lightbox.photos.length) {
      setLightbox((prev) => ({ ...prev, currentIndex: newIndex }));
    }
  };

  const handleAccept = useCallback(
    async (resourceGuid: string, index: number) => {
      const replyNote = replyNotes[resourceGuid] || '';

      // Mark as processing
      setOffers((prev) =>
        prev.map((offer, i) =>
          i === index ? { ...offer, isProcessing: true } : offer
        )
      );

      try {
        await reviewResourceOffer(resourceGuid, {
          decision: 'accepted',
          reviewNote: replyNote,
        });
        // Remove the accepted offer
        setOffers((prev) => prev.filter((_, i) => i !== index));
        // Clear the reply note
        setReplyNotes((prev) => {
          const next = { ...prev };
          delete next[resourceGuid];
          return next;
        });
        // Clear expanded state if it was this card
        if (expandedCard === resourceGuid) {
          setExpandedCard(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to accept offer');
        // Remove processing state
        setOffers((prev) =>
          prev.map((offer, i) =>
            i === index ? { ...offer, isProcessing: false } : offer
          )
        );
      }
    },
    [replyNotes, expandedCard]
  );

  const handleReject = useCallback(
    async (resourceGuid: string, index: number) => {
      const replyNote = replyNotes[resourceGuid] || '';

      // Mark as processing
      setOffers((prev) =>
        prev.map((offer, i) =>
          i === index ? { ...offer, isProcessing: true } : offer
        )
      );

      try {
        await reviewResourceOffer(resourceGuid, {
          decision: 'rejected',
          reviewNote: replyNote,
        });
        // Remove the rejected offer
        setOffers((prev) => prev.filter((_, i) => i !== index));
        // Clear the reply note
        setReplyNotes((prev) => {
          const next = { ...prev };
          delete next[resourceGuid];
          return next;
        });
        // Clear expanded state if it was this card
        if (expandedCard === resourceGuid) {
          setExpandedCard(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reject offer');
        // Remove processing state
        setOffers((prev) =>
          prev.map((offer, i) =>
            i === index ? { ...offer, isProcessing: false } : offer
          )
        );
      }
    },
    [replyNotes, expandedCard]
  );

  const calculateDaysRemaining = (expiresAt: string | null): number | null => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiryDate = new Date(expiresAt);
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getOfferTypeLabel = (offerType: string): string => {
    switch (offerType?.toLowerCase()) {
      case 'loan':
        return t('contributions.offerType.loan');
      case 'donate':
      case 'donation':
        return t('contributions.offerType.donation');
      case 'flexible':
      case 'either':
        return t('contributions.offerType.either');
      default:
        return offerType || '';
    }
  };

  const getOfferTypeBadgeClass = (offerType: string): string => {
    const lower = offerType?.toLowerCase() || '';
    if (lower === 'loan') return styles.badgeLoan;
    if (lower === 'donate' || lower === 'donation') return styles.badgeDonation;
    if (lower === 'flexible' || lower === 'either') return styles.badgeFlexible;
    return styles.badgeDefault;
  };

  const getExpirationText = (daysRemaining: number): string => {
    if (daysRemaining < 3) {
      return t('contributions.expiresSoon');
    }
    return t('contributions.expires', { date: daysRemaining });
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleCardExpanded = (resourceGuid: string) => {
    setExpandedCard((prev) => (prev === resourceGuid ? null : resourceGuid));
  };

  const renderResourceOffersSection = () => {
    const resourceOffers = offers;
    const count = resourceOffers.length;
    const isExpanded = expandedSections.resourceOffers;

    return (
      <div className={styles.section}>
        <button
          className={styles.sectionHeader}
          onClick={() => toggleSection('resourceOffers')}
          aria-expanded={isExpanded}
        >
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>{t('contributions.resourceOffers')}</h3>
            <span className={styles.countBadge}>{count}</span>
          </div>
          <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
            {'▼'}
          </span>
        </button>

        {isExpanded && (
          <div className={styles.sectionContent}>
            {resourceOffers.length === 0 ? (
              <div className={styles.placeholderMessage}>
                {t('contributions.noResourceOffers')}
              </div>
            ) : (
              <div className={styles.cardGroup}>
                {resourceOffers.map((offer, index) => {
                  const daysRemaining = calculateDaysRemaining(offer.offer_expires_at);
                  const expirationText = daysRemaining !== null ? getExpirationText(daysRemaining) : null;
                  const isExpiringSoon = daysRemaining !== null && daysRemaining < 3;
                  const categoryEmoji = CATEGORY_EMOJI[offer.category?.toLowerCase() || 'other'] || '📦';
                  const isCardExpanded = expandedCard === offer.resource_guid;
                  const hasPhotos = offer.photos && offer.photos.length > 0;
                  const firstPhoto = hasPhotos ? offer.photos.sort((a, b) => a.sort_order - b.sort_order)[0] : null;

                  return (
                    <div
                      key={offer.resource_guid || index}
                      className={`${styles.card} ${isCardExpanded ? styles.cardExpanded : ''}`}
                      data-processing={offer.isProcessing ? 'true' : 'false'}
                    >
                      {/* Collapsed view */}
                      <div className={styles.cardCollapsedHeader}>
                        {/* Photo thumbnail */}
                        {hasPhotos && firstPhoto ? (
                          <button
                            className={styles.photoThumbnail}
                            onClick={() => openLightbox(offer.photos, offer.label)}
                            title={t('contributions.clickToEnlarge')}
                            aria-label={`${t('contributions.photoOf')} ${offer.label}`}
                          >
                            <img src={firstPhoto.url} alt={offer.label} />
                            {offer.photos.length > 1 && (
                              <span className={styles.photoCount}>
                                +{offer.photos.length - 1}
                              </span>
                            )}
                          </button>
                        ) : (
                          <div className={styles.photoPlaceholder}>{categoryEmoji}</div>
                        )}

                        {/* Main info */}
                        <div className={styles.collapsedInfo}>
                          <div className={styles.labelRow}>
                            <h4 className={styles.resourceLabel}>{offer.label}</h4>
                            {offer.offer_type && (
                              <span className={`${styles.offerTypeBadge} ${getOfferTypeBadgeClass(offer.offer_type)}`}>
                                {getOfferTypeLabel(offer.offer_type)}
                              </span>
                            )}
                          </div>

                          <div className={styles.collapsedMeta}>
                            <div className={styles.metaRow}>
                              <span className={styles.metaItem}>
                                {t('contributions.offeredBy')}: {resolveUserName(offer.user_guid)}
                              </span>
                              {expirationText && (
                                <span
                                  className={`${styles.metaItem} ${isExpiringSoon ? styles.metaItemExpiringSoon : ''}`}
                                >
                                  {expirationText}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expand chevron */}
                        <button
                          className={`${styles.expandChevron} ${isCardExpanded ? styles.expandChevronOpen : ''}`}
                          onClick={() => toggleCardExpanded(offer.resource_guid)}
                          aria-expanded={isCardExpanded}
                          aria-label="Toggle offer details"
                        >
                          {'▼'}
                        </button>
                      </div>

                      {/* Expanded view */}
                      {isCardExpanded && (
                        <div className={styles.cardExpandedContent}>
                          {/* Resource Details Block */}
                          {offer.description && (
                            <div className={styles.detailBlock}>
                              <h5 className={styles.blockTitle}>{t('contributions.resourceDetails')}</h5>
                              <p className={styles.blockText}>{offer.description}</p>
                            </div>
                          )}

                          {/* Lending Terms Block */}
                          {offer.conditions && (
                            <div className={`${styles.detailBlock} ${styles.detailBlockWarning}`}>
                              <h5 className={styles.blockTitle}>{t('contributions.lendingTerms')}</h5>
                              <p className={styles.blockText}>{offer.conditions}</p>
                            </div>
                          )}

                          {/* Note from offerer Block */}
                          {offer.offer_notes && (
                            <div className={`${styles.detailBlock} ${styles.detailBlockNote}`}>
                              <h5 className={styles.blockTitle}>
                                {t('contributions.noteFrom', { name: resolveUserName(offer.user_guid) })}
                              </h5>
                              <p className={styles.blockText}>{offer.offer_notes}</p>
                            </div>
                          )}

                          {/* Reply textarea */}
                          {canEdit && (
                            <div className={styles.replySection}>
                              <label htmlFor={`reply-${offer.resource_guid}`} className={styles.replyLabel}>
                                {t('contributions.replyNote')}
                              </label>
                              <textarea
                                id={`reply-${offer.resource_guid}`}
                                className={styles.replyTextarea}
                                placeholder={t('contributions.replyNotePlaceholder')}
                                value={replyNotes[offer.resource_guid] || ''}
                                onChange={(e) =>
                                  setReplyNotes((prev) => ({
                                    ...prev,
                                    [offer.resource_guid]: e.target.value,
                                  }))
                                }
                                disabled={offer.isProcessing}
                              />
                            </div>
                          )}

                          {/* Action buttons */}
                          {canEdit && (
                            <div className={styles.cardActionButtons}>
                              <button
                                className={styles.acceptButton}
                                onClick={() => handleAccept(offer.resource_guid || '', index)}
                                disabled={offer.isProcessing}
                                aria-label={t('contributions.accept')}
                              >
                                {offer.isProcessing ? t('contributions.accepting') : t('contributions.accept')}
                              </button>
                              <button
                                className={styles.rejectButton}
                                onClick={() => handleReject(offer.resource_guid || '', index)}
                                disabled={offer.isProcessing}
                                aria-label={t('contributions.reject')}
                              >
                                {offer.isProcessing ? t('contributions.rejecting') : t('contributions.reject')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRoleApplicationsSection = () => {
    const isExpanded = expandedSections.roleApplications;

    return (
      <div className={styles.section}>
        <button
          className={styles.sectionHeader}
          onClick={() => toggleSection('roleApplications')}
          aria-expanded={isExpanded}
        >
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>{t('contributions.roleApplications')}</h3>
            <span className={styles.countBadge}>0</span>
          </div>
          <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
            {'▼'}
          </span>
        </button>

        {isExpanded && (
          <div className={styles.sectionContent}>
            <div className={styles.placeholderMessage}>
              {t('contributions.noRoleApplications')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContentSubmissionsSection = () => {
    const isExpanded = expandedSections.contentSubmissions;

    return (
      <div className={styles.section}>
        <button
          className={styles.sectionHeader}
          onClick={() => toggleSection('contentSubmissions')}
          aria-expanded={isExpanded}
        >
          <div className={styles.sectionTitleRow}>
            <h3 className={styles.sectionTitle}>{t('contributions.contentSubmissions')}</h3>
            <span className={styles.countBadge}>0</span>
          </div>
          <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
            {'▼'}
          </span>
        </button>

        {isExpanded && (
          <div className={styles.sectionContent}>
            <div className={styles.placeholderMessage}>
              {t('contributions.noContentSubmissions')}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <p>{t('contributions.loading')}</p>
        </div>
      </div>
    );
  }

  const totalCount = offers.length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('contributions.title')}</h2>
        <div className={styles.badge}>
          {totalCount > 0
            ? t('contributions.pending', { count: totalCount })
            : t('contributions.nonePending')}
        </div>
      </div>

      {error && (
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      )}

      {totalCount === 0 && !error ? (
        <div className={styles.emptyState}>
          <p>{t('contributions.empty')}</p>
        </div>
      ) : (
        <div className={styles.sectionsContainer}>
          {renderResourceOffersSection()}
          {renderRoleApplicationsSection()}
          {renderContentSubmissionsSection()}
        </div>
      )}

      {/* Photo Lightbox */}
      {lightbox.isOpen && (
        <div className={styles.lightbox} ref={lightboxRef}>
          <div className={styles.lightboxContent}>
            {/* Close button */}
            <button
              className={styles.lightboxClose}
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              {'✕'}
            </button>

            {/* Main image */}
            <div className={styles.lightboxMainImage}>
              <img
                src={lightbox.photos[lightbox.currentIndex]?.url}
                alt={`${lightbox.resourceLabel} photo ${lightbox.currentIndex + 1}`}
              />
            </div>

            {/* Navigation arrows */}
            {lightbox.photos.length > 1 && (
              <>
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
                  onClick={() => navigatePhoto(-1)}
                  disabled={lightbox.currentIndex === 0}
                  aria-label="Previous photo"
                >
                  {'‹'}
                </button>
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
                  onClick={() => navigatePhoto(1)}
                  disabled={lightbox.currentIndex === lightbox.photos.length - 1}
                  aria-label="Next photo"
                >
                  {'›'}
                </button>
              </>
            )}

            {/* Thumbnail strip */}
            {lightbox.photos.length > 1 && (
              <div className={styles.lightboxThumbnails}>
                {lightbox.photos.map((photo, idx) => (
                  <button
                    key={idx}
                    className={`${styles.lightboxThumbnail} ${
                      idx === lightbox.currentIndex ? styles.lightboxThumbnailActive : ''
                    }`}
                    onClick={() => setLightbox((prev) => ({ ...prev, currentIndex: idx }))}
                    aria-label={`Photo ${idx + 1} of ${lightbox.photos.length}`}
                    aria-current={idx === lightbox.currentIndex ? 'true' : 'false'}
                  >
                    <img src={photo.url} alt={`Thumbnail ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Counter */}
            {lightbox.photos.length > 1 && (
              <div className={styles.lightboxCounter}>
                {lightbox.currentIndex + 1} / {lightbox.photos.length}
              </div>
            )}
          </div>
        </div>
      )}

      {debugMode && offers.length > 0 && (
        <div className={styles.debugPanel}>
          <p>Debug: {offers.length} resource offers loaded</p>
        </div>
      )}
    </div>
  );
};
